/* eslint-disable camelcase, max-lines */
import { omit, snakeCase } from 'lodash-es';

import { marketingTrackingEmitterEvent } from '../../common/emitters/marketingTrackingEmitter';
import { SupplyClassification } from '../../common/interfaces/entities';
import { TrackingService } from '../../common/tracking/trackingService';
import {
  trackClickEvent,
  trackModalViewedEvent,
  trackPageViewedEvent,
} from '../GlintsTracking/tracking';
import { JobDraftFormState, JobFormState } from './common/constants';

const transformJobDraftData = (
  jobDraftData: Partial<JobFormState | JobDraftFormState>
) => ({
  job_draft_id: jobDraftData.jobDraftId,
  job_draft_state: jobDraftData,
  // Mobile steps start at 0 for consistency with mobile implementations
  post_job_step: jobDraftData.step ? jobDraftData.step - 1 : jobDraftData.step,
  post_job_type: jobDraftData.isEdit ? 'edit' : 'create',
});

export const trackJobPostStepViewedEvent = (properties: {
  jobDraftData: Partial<JobFormState | JobDraftFormState>;
}) => {
  const transformedProperties = transformJobDraftData(properties.jobDraftData);
  TrackingService.logEvent('Job Post Step Viewed', transformedProperties);

  trackPageViewedEvent({
    bypassSamePageCheck: true,
    properties: transformedProperties,
  });
};

export const trackJobPostFieldChangeEvent = (properties: {
  fieldName: string;
  jobDraftData: JobFormState;
}) => {
  const transformedProperties = transformJobDraftData(properties.jobDraftData);

  TrackingService.logEvent('Job Post Field Changed', {
    field_name: properties.fieldName,
    ...transformedProperties,
  });

  trackClickEvent({
    elementName: properties.fieldName,
    properties: transformedProperties,
  });
};

export const trackJobTitleChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_title',
    jobDraftData,
  });
};

export const trackJobLocationChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_location',
    jobDraftData,
  });
};

export const trackJobGenderChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_gender',
    jobDraftData,
  });
};

export const trackJobAgeChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_age',
    jobDraftData,
  });
};

export const trackJobSkillsChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_skills',
    jobDraftData,
  });
};

export const trackJobEducationLevelChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_education_level',
    jobDraftData,
  });
};

export const trackJobWorkExperienceChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_work_experience',
    jobDraftData,
  });
};

export const trackJobMatchCriteriaChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_match_criteria',
    jobDraftData,
  });
};

export const trackJobCommitmentTypeChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_commitment_type',
    jobDraftData,
  });
};

export const trackWorkArrangementChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_work_arrangement',
    jobDraftData,
  });
};

export const trackWorkDetailsChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_work_details',
    jobDraftData,
  });
};

export const trackSalaryChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({ fieldName: 'post_job_salary', jobDraftData });
};

export const trackProfilePhotoRequiredChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_profile_picture_required',
    jobDraftData,
  });
};
export const trackResumeRequiredChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_resume_required',
    jobDraftData,
  });
};

export const trackDescriptionGenerate = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_desc_generate',
    jobDraftData,
  });
};

export const trackDescriptionChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_description',
    jobDraftData,
  });
};

export const trackScreenQuestionsChange = (jobDraftData: JobFormState) => {
  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_screen_questions',
    jobDraftData,
  });
};

export const trackJobCreationPageViewedEvent = () => {
  TrackingService.logEvent('Job Creation Page Viewed');
};

export const trackPostJobClickedEvent = (properties: {
  jobDraftId?: string;
  jobDraftData: JobFormState;
}) => {
  const transformedProperties = {
    job_draft_id: properties.jobDraftId,
  };

  TrackingService.logEvent('Post Job Clicked', transformedProperties);
  trackClickEvent({
    elementName: 'post_job_submitted',
    properties: transformJobDraftData(properties.jobDraftData),
  });
  marketingTrackingEmitterEvent.emit({
    event: 'post_job_submitted',
    payload: transformedProperties,
  });
};

export const trackPostJobCreatedEvent = (properties: {
  jobId: string;
  jobDraftId?: string;
  companyId?: string;
  country?: string | null;
  companyVerificationStatus?: string | null;
  jobStatus: string;
  empAllUnverifiedCompaniesSgMyPostJobsEnabled: boolean;
}) => {
  const transformedProperties = {
    job_id: properties.jobId,
    job_draft_id: properties.jobDraftId,
    company_id: properties.companyId,
    country: properties.country,
    company_verification_status: properties.companyVerificationStatus,
    job_status: properties.jobStatus,
    emp_all_unverified_companies_sg_my_post_jobs_enabled:
      properties.empAllUnverifiedCompaniesSgMyPostJobsEnabled,
  };

  TrackingService.logEvent('Post Job Created', transformedProperties);
  marketingTrackingEmitterEvent.emit({
    event: 'post_job_created',
    payload: transformedProperties,
  });
};

export const trackCloseJobClickedEvent = (properties: {
  jobId: string;
  jobStatus: string;
  page: string;
}) => {
  const transformedProperties = {
    job_id: properties.jobId,
    job_status: properties.jobStatus,
    page: properties.page,
  };

  TrackingService.logEvent('Close Job Clicked', transformedProperties);
  trackClickEvent({
    elementName: 'close_job',
    properties: {
      job_id: properties.jobId,
    },
  });
};

export const trackCloseJobPopUpCancelClickedEvent = (properties: {
  jobId: string;
  jobStatus: string;
  page: string;
}) => {
  const transformedProperties = {
    job_id: properties.jobId,
    job_status: properties.jobStatus,
    page: properties.page,
  };

  TrackingService.logEvent(
    'Close Job Pop Up Cancel Clicked',
    transformedProperties
  );
  trackClickEvent({
    elementName: 'close_job_popup_cancel',
    properties: {
      job_id: properties.jobId,
    },
  });
};

export const trackCloseJobPopUpConfirmClickedEvent = (properties: {
  jobId: string;
  jobStatus: string;
  page: string;
}) => {
  const transformedProperties = {
    job_id: properties.jobId,
    job_status: properties.jobStatus,
    page: properties.page,
  };

  TrackingService.logEvent(
    'Close Job Pop Up Confirm Clicked',
    transformedProperties
  );
  trackClickEvent({
    elementName: 'close_job_popup_confirm',
    properties: {
      job_id: properties.jobId,
    },
  });
};

export const trackJobCheckInEvent = (
  event:
    | 'viewed'
    | 'answer_selected'
    | 'submit_clicked'
    | 'dismissed'
    | 'submitted'
    | 'submit_failed',
  properties: Record<string, unknown> & { modal_id: string }
) => {
  const eventName = `job_check_in_${event}`;
  TrackingService.logEvent(eventName, properties);
  if (event === 'viewed') {
    trackModalViewedEvent(properties.modal_id, properties);
  } else if (
    event === 'answer_selected' ||
    event === 'submit_clicked' ||
    event === 'dismissed'
  ) {
    trackClickEvent({
      elementName: eventName,
      pageName: properties.modal_id,
      useTargetPageName: true,
      properties,
    });
  }
  // Submission outcomes remain on TrackingService; they are not UI clicks.
};

export const trackEditJobPageViewedEvent = (properties: {
  jobId: string;
  jobStatus: string;
  jobType: string;
}) => {
  const transformedProperties = {
    job_id: properties.jobId,
    job_status: properties.jobStatus,
    job_type: properties.jobType,
  };

  TrackingService.logEvent('Edit Job Page Viewed', transformedProperties);
};

export const trackEditJobTabClickedEvent = (properties: {
  jobId: string;
  jobStatus: string;
  tabClicked: number;
  tabName: string;
}) => {
  const transformedProperties = {
    job_id: properties.jobId,
    job_status: properties.jobStatus,
    tab_clicked: properties.tabClicked,
    tab_name: properties.tabName,
  };

  TrackingService.logEvent('Edit Job Tab Clicked', transformedProperties);
  trackClickEvent({
    elementName: 'edit_job_tab',
    properties: {
      job_id: properties.jobId,
      tab_name: properties.tabName,
    },
  });
};

export const trackJobEditSaveChangesClickedEvent = (properties: {
  jobId: string;
  jobStatus: string;
}) => {
  const transformedProperties = {
    job_id: properties.jobId,
    job_status: properties.jobStatus,
  };

  TrackingService.logEvent(
    'Job Edit Save Changes Clicked',
    transformedProperties
  );
  trackClickEvent({
    elementName: 'job_edit_save_changes',
    properties: transformedProperties,
  });
};

export const trackJobPostAlertShownEvent = (properties: {
  jobId: string;
  jobStatus: string;
}) => {
  const transformedProperties = {
    job_id: properties.jobId,
    job_status: properties.jobStatus,
    job_classification: 'regular_job',
  };

  TrackingService.logEvent('Job Post Alert Shown', transformedProperties);
};

type JobReopenProperty = {
  jobId: string;
  jobStatus: string;
  jobType: string;
  jobCategory: number;
  jobCountry: string;
  jobCity: string;
  numView?: number;
  numPending?: number;
  numShortlist?: number;
  numRejected?: number;
  hierarchicalJobCategoryId: string;
  page: string;
};

const transformJobReopenProperty = (properties: JobReopenProperty) => ({
  job_id: properties.jobId,
  job_status: properties.jobStatus,
  job_type: properties.jobType,
  job_category: properties.jobCategory,
  job_country: properties.jobCountry,
  job_city: properties.jobCity,
  num_view: properties.numView,
  job_classification: 'regular_job',
  num_pending: properties.numPending,
  num_shortlist: properties.numShortlist,
  num_rejected: properties.numRejected,
  hierarchical_job_category_id: properties.hierarchicalJobCategoryId,
  page: properties.page,
});

export const trackExtendJobButtonClickedEvent = (
  properties: JobReopenProperty
) => {
  TrackingService.logEvent(
    'Extend Job Button Clicked',
    transformJobReopenProperty(properties)
  );
  trackClickEvent({
    elementName: 'extend_job',
    properties: omit(transformJobReopenProperty(properties), ['page']),
  });
};

export const trackReopenJobButtonClickedEvent = (
  properties: JobReopenProperty
) => {
  TrackingService.logEvent(
    'Reopen Job Button Clicked',
    transformJobReopenProperty(properties)
  );
  trackClickEvent({
    elementName: 'reopen_job',
    properties: omit(transformJobReopenProperty(properties), ['page']),
  });
};

export const trackReopenJobConfirmClickedEvent = (
  properties: JobReopenProperty
) => {
  TrackingService.logEvent(
    'Reopen Job Confirm Clicked in Popup',
    transformJobReopenProperty(properties)
  );
};

export const trackReopenJobEditClickedEvent = (
  properties: JobReopenProperty
) => {
  TrackingService.logEvent(
    'Reopen Job Edit Clicked in Popup',
    transformJobReopenProperty(properties)
  );
};

export const trackJobPreviewButtonClickedEvent = (properties: {
  jobPostPage: string;
  jobDraftId: string;
  jobId: string;
  jobStatus: string;
}) => {
  const transformedProperties = {
    job_post_page: properties.jobPostPage,
    job_draft_id: properties.jobDraftId,
    job_id: properties.jobId,
    job_status: properties.jobStatus,
  };

  TrackingService.logEvent('Job Preview Button Clicked', transformedProperties);
  trackClickEvent({
    elementName: 'job_preview',
    properties: {
      job_draft_id: properties.jobDraftId || null,
      job_id: properties.jobId || null,
      job_status: properties.jobStatus || null,
    },
  });
};

export const trackPostJobButtonEnabledEvent = (properties: {
  jobDraftId: string;
}) => {
  const transformedProperties = {
    job_draft_id: properties.jobDraftId,
  };

  TrackingService.logEvent('Post Job Button Enabled', transformedProperties);
};

export const trackJobTitleFilled = (props: { title: string | undefined }) => {
  TrackingService.logEvent('job_title_filled', {
    job_title: snakeCase(props.title || 'null'),
  });
};

export const trackAutoRecommendedJobRole = (props: {
  recommendedRole: string | undefined;
}) => {
  TrackingService.logEvent('job_role_recommended', {
    recommended_role: snakeCase(props.recommendedRole || 'null'),
  });
  trackClickEvent({
    elementName: 'job_role_recommended',
    properties: {
      recommended_role: props.recommendedRole
        ? snakeCase(props.recommendedRole)
        : null,
    },
  });
};

export const trackJobRoleUpdated = (props: {
  recommendedJobRoles: string[];
  updatedJobRole: string;
  isRecommended: boolean;
  jobDraftData: JobFormState;
}) => {
  const transformedProperties = {
    recommended_job_role:
      props.recommendedJobRoles.map(snakeCase).join(', ') || null,
    updated_job_role: snakeCase(props.updatedJobRole) || null,
    is_recommended: props.isRecommended,
  };
  TrackingService.logEvent('job_role_updated', transformedProperties);
  trackClickEvent({
    elementName: 'job_role_updated',
    properties: transformedProperties,
  });

  trackJobPostFieldChangeEvent({
    fieldName: 'post_job_role',
    jobDraftData: props.jobDraftData,
  });
};

export const trackPostBoostSuccessViewed = (props: {
  jobId: string;
  source: 'JOB_POSTED_POST_BOOST' | 'JOB_EXTENDED_POST_BOOST';
}) => {
  TrackingService.logEvent('Post Boost Success Viewed', {
    job_id: props.jobId,
    source: props.source,
  });
};

export const trackJobExtendedModalViewed = (props: {
  jobId: string;
  variant: SupplyClassification;
  source: 'JOB_EXTENDED';
}) => {
  TrackingService.logEvent('Job Extended Modal Viewed', {
    job_id: props.jobId,
    variant: props.variant,
    source: props.source,
  });
};

export const trackJobPostRetried = (props: {
  attempt: number;
  statusCode?: number;
  errorCode?: string;
  userId?: string;
  companyId?: string;
}) => {
  TrackingService.logEvent('Job Post Retried', {
    attempt: props.attempt,
    status_code: props.statusCode,
    error_code: props.errorCode,
    user_id: props.userId,
    company_id: props.companyId,
  });
};

export const trackJobDraftRetried = (props: {
  attempt: number;
  statusCode?: number;
  errorCode?: string;
  userId?: string;
  companyId?: string;
}) => {
  TrackingService.logEvent('Job Draft Retried', {
    attempt: props.attempt,
    status_code: props.statusCode,
    error_code: props.errorCode,
    user_id: props.userId,
    company_id: props.companyId,
  });
};
